import { useContext, useEffect, useState, useRef, Fragment } from "react";
import { Context } from "Context";
import Separator from "./Separator";
import Button from "./Button";
import Icon from "./Icon";

const bodyOnModalOpen = "help-overflow-hidden";
const escapeKey = 27;
const tabKey = 9;
const descriptionId = "modalDescriptionId";
const titleId = "modalTitleId";

const helpModalClass = "help-modal";
const helpModalContainerClass = `${helpModalClass}__container`;
const helpModalTitleClass = `${helpModalClass}__title`;
const helpModalCloseButtonClass = `${helpModalClass}__close-button`;
const helpModalContentClass = `${helpModalClass}__content`;
const helpModalFooterClass = `${helpModalClass}__footer`;

const handleTabKey = ({ lastElement, firstElement }) => (e) => {
	if (!e.shiftKey && document.activeElement === lastElement) {
		e.preventDefault();
		firstElement.focus();
	}

	if (e.shiftKey && document.activeElement === firstElement) {
		e.preventDefault();
		lastElement.focus();
	}
};

const renderContent = ({
	title,
	description,
	content,
	primaryButton,
	secondaryButton,
	href,
	target,
	primaryButtonAnalyticsLinkId,
	secondaryButtonAnalyticsLinkId,
	primaryButtonWebLink: primaryButtonLinkContent = {},
	secondaryButtonWebLink: secondaryButtonLinkContent = {},
	handleClose,
	brand,
	showSeparator = true,
	closeButtonIcon,
	trackHover,
	renderPrimaryButtonFirst,
}) => {
	if (href) {
		primaryButtonLinkContent.href = href;
		if (target) {
			primaryButtonLinkContent.target = target;
		}
		if (primaryButton) {
			primaryButtonLinkContent.title = primaryButton;
		}
		if (primaryButtonAnalyticsLinkId) {
			primaryButtonLinkContent.analyticsLinkId = primaryButtonAnalyticsLinkId;
		}
	}

	const renderSecondaryButton = secondaryButton && (
		<Button
			link={!!secondaryButtonLinkContent.href}
			linkContent={{ ...secondaryButtonLinkContent, trackHover }}
			title={secondaryButton}
			analyticsLinkId={secondaryButtonAnalyticsLinkId}
			trackHover={trackHover}
			onClick={handleClose}
			secondary={true}
		/>
	);

	const renderPrimaryButton = primaryButton && (
		<Button
			link={!!primaryButtonLinkContent.href}
			linkContent={{ ...primaryButtonLinkContent, trackHover }}
			title={primaryButton}
			analyticsLinkId={primaryButtonAnalyticsLinkId}
			trackHover={trackHover}
			onClick={handleClose}
		/>
	);

	return (
		<Fragment>
			{closeButtonIcon && (
				<button className={helpModalCloseButtonClass} onClick={handleClose}>
					<Icon icon={closeButtonIcon} />
				</button>
			)}
			{title && (
				<h2
					className={`${helpModalTitleClass} ${helpModalTitleClass}--${brand}`}
					tabIndex="-1"
					id={titleId}
				>
					{title}
				</h2>
			)}
			{description && (
				<div
					className={`${helpModalContentClass} ${helpModalContentClass}--${brand}`}
					dangerouslySetInnerHTML={{ __html: description }}
					id={descriptionId}
				/>
			)}
			{content && (
				<div
					className={`${helpModalContentClass} ${helpModalContentClass}--${brand}`}
					tabIndex="-1"
				>
					{content}
				</div>
			)}
			{(primaryButton || secondaryButton) && (
				<Fragment>
					{showSeparator && <Separator />}
					<div
						className={`${helpModalFooterClass} ${helpModalFooterClass}--${brand}`}
					>
						{renderPrimaryButtonFirst ? (
							<Fragment>
								{renderPrimaryButton}
								{renderSecondaryButton}
							</Fragment>
						) : (
							<Fragment>
								{renderSecondaryButton}
								{renderPrimaryButton}
							</Fragment>
						)}
					</div>
				</Fragment>
			)}
		</Fragment>
	);
};

const Modal = () => {
	const [focusableModalElements, setFocusableModalElements] = useState(null);
	const [firstElement, setFirstElement] = useState(null);
	const [lastElement, setLastElement] = useState(null);

	const modalRef = useRef(null);
	const {
		modal,
		setModal,
		config: { brand, focusableElements },
	} = useContext(Context);

	const handleClose = (e) => {
		if (e?.keyCode === escapeKey && modal.disableEscapeDismissal) {
			return null;
		}

		if (modal?.triggerElementRef?.current) {
			modal.triggerElementRef.current.focus();
		}
		setModal(false);
	};

	useEffect(() => {
		if (modalRef.current) {
			setFocusableModalElements(
				modalRef.current.querySelectorAll(focusableElements)
			);
		}
	}, [modal, focusableElements]);

	useEffect(() => {
		if (focusableModalElements) {
			setFirstElement(focusableModalElements[0]);
			setLastElement(focusableModalElements[focusableModalElements.length - 1]);
		}
	}, [focusableModalElements]);

	const keyListener = (e) => {
		if (![escapeKey, tabKey].includes(e.keyCode)) return null;
		const listener =
			e.keyCode === tabKey
				? handleTabKey({ lastElement, firstElement })
				: handleClose;
		return listener(e);
	};

	const handleClickOutside = (event) => {
		if (
			modal.dismissible &&
			!modal.disableEscapeDismissal &&
			modalRef.current?.contains(event.target)
		) {
			if (event && modalRef.current?.children?.[0]?.getBoundingClientRect) {
				const boundingClientRect = modalRef.current.children[0].getBoundingClientRect();
				if (
					event.clientX < boundingClientRect.x ||
					event.clientX > boundingClientRect.right ||
					event.clientY < boundingClientRect.y ||
					event.clientY > boundingClientRect.bottom
				) {
					setModal(false);
				}
			}
		}
	};

	useEffect(() => {
		if (modal) {
			modalRef.current.focus();
			document.body.classList.add(bodyOnModalOpen);
			document.addEventListener("click", handleClickOutside);
		}
		return () => {
			document.body.classList.remove(bodyOnModalOpen);
			window.removeEventListener("click", handleClickOutside);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modal]);

	return modal ? (
		<div
			className={`${helpModalClass} ${helpModalClass}--${brand}${
				modal.modifier ? ` ${helpModalClass}--${modal.modifier}` : ""
			}`}
			role="dialog"
			aria-modal="true"
			aria-labelledby={modal.title ? titleId : undefined}
			aria-label={
				!modal.title && modal.accessibilityTitle
					? modal.accessibilityTitle
					: undefined
			}
			aria-describedby={modal.description ? descriptionId : undefined}
			ref={modalRef}
			tabIndex="-1"
			onKeyDown={keyListener}
		>
			<div
				className={`${helpModalContainerClass} ${helpModalContainerClass}--${brand}`}
			>
				{modal && renderContent({ ...modal, handleClose, brand })}
			</div>
		</div>
	) : null;
};

export default Modal;
